import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { changePlan } from "api/plan-change";
import { getUserFriendlyErrorMessage } from "api/UserFriendlyError";
import { ReduxRootState } from "@/types/redux-store";
import { reloadPage } from "utils/browser-navigation";

type ChangePlanState = {
  pending: boolean;
};

const featureKey = "changePlan" as const;

const initialState: ChangePlanState = {
  pending: false,
};

function getFeatureState(state: ReduxRootState): ChangePlanState {
  return (
    (state as ReduxRootState & { [featureKey]?: ChangePlanState })[
      featureKey
    ] ?? initialState
  );
}

export const changePlanFx = createAsyncThunk(
  "changePlan/run",
  async ({
    planId,
    ugcLibraryAddon,
    onComplete,
  }: {
    planId: string;
    ugcLibraryAddon: boolean;
    onComplete(): void;
  }) => {
    try {
      await changePlan({ planId, ugcLibraryAddon });
      onComplete();

      if (
        window.confirm(
          "You plan was successfully changed and will take effect after you refresh the dashboard. Want to refresh now?"
        )
      ) {
        localStorage.removeItem("skedApiToken");
        reloadPage();
      } else {
        toast.success(
          "Your new plan will take effect as soon as you refresh the dashboard"
        );
      }
    } catch (error) {
      toast.error(
        getUserFriendlyErrorMessage(error, "Could not update your plan")
      );
      throw error;
    }
  }
);

const changePlanSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(changePlanFx.pending, (state) => {
      state.pending = true;
    });
    builder.addCase(changePlanFx.fulfilled, (state) => {
      state.pending = false;
    });
    builder.addCase(changePlanFx.rejected, (state) => {
      state.pending = false;
    });
  },
});

export const changePlanReducer = changePlanSlice.reducer;

export const selectChangePlanPending = createSelector(
  getFeatureState,
  (changePlanState) => changePlanState.pending
);

export function useChangePlan() {
  const dispatch = useDispatch();
  const isChangingPlan = useSelector(selectChangePlanPending);

  return {
    isChangingPlan,
    changePlan: (payload: Parameters<typeof changePlanFx>[0]) =>
      dispatch(changePlanFx(payload) as never),
  };
}
